import { useState, useEffect, useCallback } from 'react';
import { callCloudRun } from '../lib/cloudRun';
import { NpsSurveyResponses, NpsSurveyMetadata, NpsSurveyCheckResult, NpsSurveyWindow } from '@rhwb/types';
import { seasonNumberFrom, asksSurveyEligibility } from '../lib/npsSurvey';

const INITIAL_RESPONSES: NpsSurveyResponses = {
  feedback_quality: null,
  communication: null,
  relationship: null,
  recommendation: null,
  comments: '',
  rhwb_effectiveness: null,
  rhwb_knowledge_depth: null,
  rhwb_recommendation: null,
  rhwb_comments: '',
  lite_reasons: [],
  lite_rating: null,
  lite_comments: '',
  pulse_experience: '',
};

/**
 * The runner NPS survey for the current season. The season always comes from
 * /api/seasons — never the dashboard's season filter, which may be a past one —
 * and the survey is shown only while the admin-set window is open.
 */
export function useNpsSurvey(email: string, userRole: string | undefined, bypassDismissal = false) {
  const [season, setSeason] = useState('');
  const [shouldShowSurvey, setShouldShowSurvey] = useState(false);
  const [open, setOpen] = useState(false);
  const [surveyWindow, setSurveyWindow] = useState<NpsSurveyWindow | null>(null);
  const [step, setStep] = useState(0); // 0: Coach ratings, 1: Coach recommendation, 2: RHWB, 3: Pulse experience, 4: Confirmation
  const [responses, setResponses] = useState<NpsSurveyResponses>(INITIAL_RESPONSES);
  const [metadata, setMetadata] = useState<NpsSurveyMetadata | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [eligibilityCheckFailed, setEligibilityCheckFailed] = useState(false);
  const [notEligible, setNotEligible] = useState(false);

  const seasonKey = `Season ${season}`;
  const dismissedKey = `rhwb-nps-dismissed-${seasonKey}`;

  // bypassDismissal is set only by the survey page itself.
  const isSurveyRole = asksSurveyEligibility(userRole, bypassDismissal);
  // Admins and lords are eligible only if the API finds them on the roster, so
  // a refusal for them means "not a runner this season", not a broken profile.
  const rosterDecides = isSurveyRole && !asksSurveyEligibility(userRole, false);

  // Resolve the current season
  useEffect(() => {
    if (!email || !isSurveyRole) return;
    let cancelled = false;
    callCloudRun<{ current: string | null }>('/api/seasons', {})
      .then(res => {
        if (cancelled) return;
        const number = seasonNumberFrom(res.current);
        if (number) {
          setSeason(number);
        } else {
          setEligibilityCheckFailed(true);
          setCheckComplete(true);
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[NPS] Season lookup error:', err);
        setEligibilityCheckFailed(true);
        setCheckComplete(true);
      });
    return () => { cancelled = true; };
  }, [email, isSurveyRole]);

  // Check eligibility once the season is known
  useEffect(() => {
    if (!email || !userRole) return;
    if (!isSurveyRole) {
      setNotEligible(true);
      setCheckComplete(true);
      return;
    }
    if (!season) return;

    // Check session dismissal (bypass when user directly navigates to the survey page)
    if (!bypassDismissal) {
      try {
        if (sessionStorage.getItem(dismissedKey) === 'true') {
          setCheckComplete(true);
          return;
        }
      } catch { /* sessionStorage unavailable */ }
    }

    const checkEligibility = async () => {
      try {
        const result = await callCloudRun<NpsSurveyCheckResult>('/api/nps/check', { season: seasonKey });

        console.log('[NPS] Check result:', result);
        if (result.metadata) setMetadata(result.metadata);
        if (result.window) setSurveyWindow(result.window);
        setOpen(!!result.open);
        if (result.alreadySubmitted) setAlreadySubmitted(true);
        if (result.open && !result.alreadySubmitted) setShouldShowSurvey(true);
      } catch (err: any) {
        if (rosterDecides) {
          setNotEligible(true);
        } else {
          console.error('[NPS] Check error:', err);
          setEligibilityCheckFailed(true);
        }
      } finally {
        setCheckComplete(true);
      }
    };

    checkEligibility();
  }, [email, season, userRole, isSurveyRole, rosterDecides, seasonKey, dismissedKey, bypassDismissal]);

  const updateResponse = useCallback((field: keyof NpsSurveyResponses, value: number | string | string[] | null) => {
    setResponses(prev => ({ ...prev, [field]: value }));
  }, []);

  const nextStep = useCallback(() => {
    setStep(prev => Math.min(prev + 1, 4));
  }, []);

  const prevStep = useCallback(() => {
    setStep(prev => Math.max(prev - 1, 0));
  }, []);

  const dismiss = useCallback(() => {
    setShouldShowSurvey(false);
    try {
      sessionStorage.setItem(dismissedKey, 'true');
    } catch { /* sessionStorage unavailable */ }
  }, [dismissedKey]);

  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await callCloudRun<{ success: boolean }>('/api/nps/submit', {
        season: seasonKey,
        responses,
      });
      console.log('[NPS] Submit result:', result);
      if (result.success) {
        setStep(4);
      } else {
        setSubmitError(true);
      }
    } catch (err: any) {
      console.error('[NPS] Submit error:', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  }, [responses, seasonKey]);

  const close = useCallback(() => {
    setShouldShowSurvey(false);
  }, []);

  return {
    season,
    open,
    surveyWindow,
    shouldShowSurvey,
    alreadySubmitted,
    eligibilityCheckFailed,
    notEligible,
    submitError,
    step,
    setStep,
    responses,
    metadata,
    submitting,
    checkComplete,
    updateResponse,
    nextStep,
    prevStep,
    dismiss,
    submit,
    close,
  };
}
